import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Pencil, Plus, Power } from 'lucide-react'

import { ApiError } from '@/api/client'
import { locationsApi } from '@/api/endpoints'
import type { Location } from '@/api/types'
import { PageHeader } from '@/components/layout/AppLayout'
import { Alert, EmptyState } from '@/components/ui/Alert'
import { StatusBadge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/Modal'
import { Pagination } from '@/components/ui/Pagination'
import { SkeletonRows } from '@/components/ui/Spinner'
import {
  DataTable,
  EmptyRow,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ToggleChip, Toolbar } from '@/components/ui/Toolbar'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { LocationFormModal } from './LocationFormModal'

const PAGE_SIZE = 20

export function LocationsPage() {
  const queryClient = useQueryClient()
  const { notifySuccess, notifyError } = useToast()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<Location | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [toDeactivate, setToDeactivate] = useState<Location | null>(null)

  const debouncedSearch = useDebouncedValue(search)

  const query = useQuery({
    queryKey: ['locations', { page, search: debouncedSearch, includeInactive }],
    queryFn: () =>
      locationsApi.list({
        page,
        size: PAGE_SIZE,
        search: debouncedSearch || undefined,
        include_inactive: includeInactive,
      }),
    placeholderData: keepPreviousData,
  })

  const deactivation = useMutation({
    mutationFn: (location: Location) => locationsApi.deactivate(location.id),
    onSuccess: async (_, location) => {
      await queryClient.invalidateQueries({ queryKey: ['locations'] })
      notifySuccess(`Oddział „${location.name}” został dezaktywowany.`)
      setToDeactivate(null)
    },
    onError: (caught: unknown) => {
      notifyError(caught instanceof ApiError ? caught.message : 'Nie udało się dezaktywować.')
      setToDeactivate(null)
    },
  })

  const openCreate = () => {
    setEditing(null)
    setIsFormOpen(true)
  }

  const openEdit = (location: Location) => {
    setEditing(location)
    setIsFormOpen(true)
  }

  const items = query.data?.items ?? []
  const isEmpty = !query.isPending && items.length === 0

  return (
    <>
      <PageHeader
        title="Oddziały"
        description="Oddział wyznacza granicę dostępu do danych — administrator widzi wyłącznie przypisane mu lokalizacje."
        breadcrumb={['System', 'Oddziały']}
        action={
          <Button icon={<Plus size={18} />} onClick={openCreate}>
            Nowy oddział
          </Button>
        }
      />

      <Toolbar
        searchValue={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder="Szukaj po nazwie lub mieście…"
      >
        <ToggleChip
          checked={includeInactive}
          onChange={(checked) => {
            setIncludeInactive(checked)
            setPage(1)
          }}
          label="Pokaż nieaktywne"
        />
      </Toolbar>

      {query.isError && (
        <Alert tone="error" className="mb-6">
          Nie udało się wczytać listy oddziałów. Odśwież stronę i spróbuj ponownie.
        </Alert>
      )}

      <Card>
        {query.isPending ? (
          <SkeletonRows />
        ) : isEmpty ? (
          <EmptyState
            icon={<Building2 size={28} />}
            title={search ? 'Brak wyników' : 'Nie ma jeszcze żadnego oddziału'}
            description={
              search
                ? 'Zmień kryteria wyszukiwania albo wyczyść pole.'
                : 'Utwórz pierwszy oddział, aby móc przypisywać do niego konta, sale i uczniów.'
            }
            action={
              !search && (
                <Button icon={<Plus size={18} />} onClick={openCreate}>
                  Nowy oddział
                </Button>
              )
            }
          />
        ) : (
          <>
            <DataTable caption="Lista oddziałów szkoły">
              <TableHead
                columns={[
                  'Oddział',
                  'Miasto',
                  'Kontakt',
                  'Status',
                  { label: 'Akcje', srOnly: true },
                ]}
              />
              <TableBody>
                {items.map((location) => (
                  <TableRow key={location.id} muted={!location.is_active}>
                    <TableCell>
                      <p className="font-bold text-on-surface">{location.name}</p>
                      <p className="text-xs text-on-surface-variant">/{location.slug}</p>
                    </TableCell>
                    <TableCell>
                      <p>{location.city ?? '—'}</p>
                      {location.address && (
                        <p className="text-xs text-on-surface-variant">{location.address}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <p>{location.phone ?? '—'}</p>
                      {location.email && (
                        <p className="text-xs text-on-surface-variant">{location.email}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge isActive={location.is_active} />
                    </TableCell>
                    <TableCell align="right">
                      <div className="flex justify-end gap-1">
                        <IconButton
                          label={`Edytuj oddział ${location.name}`}
                          icon={<Pencil size={16} />}
                          onClick={() => openEdit(location)}
                        />
                        <IconButton
                          label={`Dezaktywuj oddział ${location.name}`}
                          icon={<Power size={16} />}
                          onClick={() => setToDeactivate(location)}
                          disabled={!location.is_active}
                          className="hover:bg-error-container hover:text-error"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <EmptyRow colSpan={5} message="Brak oddziałów." />}
              </TableBody>
            </DataTable>

            <Pagination
              page={page}
              size={PAGE_SIZE}
              total={query.data?.total ?? 0}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <LocationFormModal
        open={isFormOpen}
        location={editing}
        onClose={() => setIsFormOpen(false)}
      />

      <ConfirmDialog
        open={toDeactivate !== null}
        title="Dezaktywować oddział?"
        message={
          toDeactivate
            ? `Oddział „${toDeactivate.name}” zniknie z list wyboru, ale wszystkie dane historyczne — grafiki, obecności i rozliczenia — pozostaną nienaruszone. Oddział można później przywrócić.`
            : ''
        }
        confirmLabel="Dezaktywuj"
        isLoading={deactivation.isPending}
        onConfirm={() => toDeactivate && deactivation.mutate(toDeactivate)}
        onClose={() => setToDeactivate(null)}
      />
    </>
  )
}
